/**
 * Follow-on (second property) agreement data.
 * Waived retainer, no fixed time limit, success fee at settlement only.
 * Keep in sync with sp-sales-crm-be/src/routers/agreements_v2/data/agreement_follow_on_data.py
 */

import { scheduleItems as baseScheduleItems, termsAndConditions as baseTermsAndConditions } from './agreementData';

export const FOLLOW_ON_AGREEMENT_IDS = [
  '23c5b4dd-8eda-48ef-b095-55ff70d0dc75', // Daniel Cox - client 28226
  'f98cda3e-3b57-4bb3-9522-d65f94404e15', // Thiyaga Hari - client 16927
];

const followOnScheduleOverrides: Record<number, { id: number; title: string; content: string }> = {
  4: {
    id: 4,
    title: 'Retainer',
    content:
      "Nil. The Retainer was paid under the Buyer's primary Search Property buyers agency agreement and is waived under this Agreement.",
  },
  10: {
    id: 10,
    title: 'Term',
    content:
      'means the period commencing from the date this Agreement is signed by you and ending on the Settlement Date. There is no fixed time limit for the Buyer to proceed with the Approved Acquisition.',
  },
};

const followOnTermsOverrides: Record<
  number,
  { title: string; content: string[] }
> = {
  0: {
    title: '1. Interpretation and Definitions',
    content: [
      'In this Agreement:',
      '1.1. capitalised terms are defined in accordance with the Schedule;',
      '1.2. where a party to this Agreement comprises of two or more persons, the rights and obligations of those persons pursuant to this Agreement will apply to each of them jointly and severally; and',
      '1.3. all amounts referred to in this Agreement are in Australian Dollars.',
      '1.4. This Agreement is for a second property under the two-property package agreed between the parties. The Retainer was paid under a separate primary agreement.',
    ],
  },
  4: {
    title: '5. Payment',
    content: [
      '5.1. No Retainer is payable under this Agreement.',
      '5.2. The Success Fee is only payable on settlement of the Property. Search Property will issue an invoice at settlement. Payment is due within 7 days of the invoice date.',
      '5.3. If the Buyer does not proceed with a second property, no fee is payable under this Agreement.',
    ],
  },
  5: {
    title: '6. Refunds',
    content: [
      '6.1. No Retainer is payable under this Agreement.',
      '6.2. Not applicable.',
      '6.3. The Success Fee is not refundable under any circumstances.',
    ],
  },
  6: {
    title: '7. Termination',
    content: [
      '7.1. Search Property may, in its absolute discretion, immediately terminate this Agreement without notice if the Buyer:',
      '7.1.1. engages, appoints, consults with, or accepts the services of another buyers agency in Australia other than Search Property during the Term;',
      '7.1.2. fails to pay the Success Fee when due; and',
      '7.1.3. fails to provide any information, documents or cooperation reasonably required by Search Property to complete its identity verification, source of funds checks, source of wealth checks or other anti-money laundering and counter-terrorism financing due diligence requirements.',
      '7.2. Other than in the circumstances set out in clause 7.1, neither party may terminate this Agreement during the Term.',
      '7.3. Either party may terminate this Agreement by giving 14 days written notice if the Buyer elects not to proceed with a second property, provided no Success Fee has become payable. At or after the end of the Term, either party may terminate by giving 14 days written notice.',
    ],
  },
  7: {
    title: '8. Liability and Indemnities',
    content: [
      '8.1. The Buyer will hold and keep indemnified the Agent against all actions, suits, proceedings, claims, demands, costs, and expenses whatsoever which may be taken or made against the Agent arising out of or in respect of any of the powers, duties or authorities of the Agent under this Agreement.',
      '8.2. The Buyer indemnifies the Agent for all expenses, costs, including but not limited to debt collection fees and/or legal costs and disbursements incurred by the Agent in recovering any outstanding fees under this Agreement.',
      '8.3. The Agent makes no warranties to any information or data from any third parties and expressly disclaims all liability in relation to such information or data, including but not limited to its accuracy, completeness, suitability or reliability.',
      '8.4. Any Claim made by the Client and/or the Guarantor(s) against Search Property or the Agent is limited to the Success Fee.',
    ],
  },
};

export const isFollowOnAgreement = (agreementId: string | null | undefined): boolean => {
  if (!agreementId) {
    return false;
  }
  return FOLLOW_ON_AGREEMENT_IDS.includes(agreementId);
};

export const getFollowOnScheduleItems = () =>
  baseScheduleItems.map((item) => {
    const override = followOnScheduleOverrides[item.id];
    return override ? { ...item, ...override } : item;
  });

export const getFollowOnTermsAndConditions = () =>
  baseTermsAndConditions.map((section, index) => {
    const override = followOnTermsOverrides[index];
    return override ? { ...section, ...override } : section;
  });
